import { getPersistentDPID } from './uuid';

export type LGInfo = {
  brandName?: string | null;
  ddrSize?: string | null;
  dolbyAtmos?: boolean | null;
  dolbyVision?: boolean | null;
  hdr10?: boolean | null;
  mainboardMaker?: string | null;
  manufacturer?: string | null;
  modelName?: string | null;
  oled?: boolean | null;
  platformBizType?: string | null;
  screenHeight?: number | null;
  screenWidth?: number | null;
  sdkVersion?: string | null;
  tuner?: boolean | null;
  uhd?: boolean | null;
  uhd8K?: boolean | null;
  version?: string | null;
  versionDot?: number | null;
  versionMajor?: number | null;
  versionMinor?: number | null;
  lgudid?: string | null;
  isLatEnabled?: boolean | null;
};

/**
 * Determines the identifier to use for LG TVs.
 *
 * ✔ If `lgudid` is available → use it
 * ✔ If missing → fallback to persistent DPID (stored in localStorage)
 *
 * @function resolveLGId
 * @param {string | null} lgudid - The LG UDID retrieved from the WebOS API.
 * @returns {{ tvIdType: 'lgudid' | 'dpid', tvId: string }}
 *   - `tvIdType`: the chosen type of identifier
 *   - `tvId`: the actual value to use
 */
export const resolveLGId = (lgudid: string | null) => {
  if (lgudid) {
    return {
      tvIdType: 'lgudid' as const,
      tvId: lgudid,
    };
  }

  return {
    tvIdType: 'dpid' as const,
    tvId: getPersistentDPID(),
  };
};

/**
 * Fetches the LG WebOS device information using the official WebOS API.
 *
 * ✔ Calls `window.webOS.deviceInfo` (async callback API)
 * ✔ Returns `null` if the API does not exist or fails
 * ✔ Returns a partial `LGInfo` object if successful
 *
 * @function getLGDeviceInfo
 * @returns {Promise<Partial<LGInfo> | null>}
 *   Resolves to device info or null on failure.
 */
export const getLGDeviceInfo = (): Promise<Partial<LGInfo> | null> =>
  new Promise((resolve) => {
    if (!window.webOS?.deviceInfo) return resolve(null);

    window.webOS.deviceInfo(
      (ok: any) => resolve(ok),
      () => resolve(null)
    );
  });

/**
 * Retrieves the LG UDID using the WebOS Dev API.
 *
 * ✔ Calls `window.webOSDev.LGUDID()`
 * ✔ Returns the UDID string when successful
 * ✔ Returns `null` when the API is missing or fails
 *
 * @function getLGUDID
 * @returns {Promise<string | null>}
 *   Resolves with the UDID or null if unavailable.
 */
export const getLGUDID = (): Promise<string | null> =>
  new Promise((resolve) => {
    if (!window.webOSDev?.LGUDID) return resolve(null);

    window.webOSDev.LGUDID({
      onSuccess: (res: any) => resolve(res?.id ?? null),
      onFailure: () => resolve(null),
    });
  });
